import React from "react";
import clonedeep from "lodash.clonedeep";
//Limitless imports
import { log as pilog, ClientContext } from "@limitless/common/utils";
import { classifyMessage } from "../../../portal";
import { useNotificationContext } from "@limitless/common/hooks/notification/NotificationContext";
import { useExpertMessageContext } from "../expert-message/ExpertMessageContext";
import { getMessageClassificationPayload } from "./MessageClassificationHelper";
export const INITIAL_MESSAGE_CLASSIFICATION_STATE = {
  msgClassification: {
    classificationCode: "",
    classificationCodeDescription: "",
    attributes: [],
    pristine: true
  },
  dialogueNote: {
    value: "",
    pristine: true
  },
  tags: {
    value: [],
    pristine: true
  },
  responseCode: {
    label: "",
    value: "",
    pristine: true
  },
  expertPrefVote: {
    value: "",
    pristine: true
  },
  hasTouched: false
};
export const getInitialClassificationState = message => {
  const initialState = clonedeep(INITIAL_MESSAGE_CLASSIFICATION_STATE);
  if (message.responseCode) {
    initialState.responseCode.label = message.responseCodeDesc;
    initialState.responseCode.value = message.responseCode;
    initialState.responseCode.pristine = false;
  }
  if (message.msgClassification) {
    initialState.msgClassification = {
      ...initialState.msgClassification,
      ...message.msgClassification
    };
    initialState.msgClassification.pristine = false;
  }
  if (message.editableTags.length !== 0) {
    initialState.tags.value = message.editableTags;
    initialState.tags.pristine = false;
  }
  return initialState;
};
const liveUpdateFields = ["responseCode", "responseCodeDesc", "tags", "msgClassification", "expertPrefVote"];
export const useMessageClassification = () => {
  const log = pilog.getLogger("useMessageClassification");
  const {
    addFailureNotification
  } = useNotificationContext();
  const {
    message,
    toggleMessageLoading,
    clearMessage,
    clearMessageAndUnclaim
  } = useExpertMessageContext();
  const [messageClassification, setMessageClassification] = React.useState(getInitialClassificationState(message));

  //if the messageId changes, reset the message classification context
  React.useEffect(() => {
    resetClassificationFields();
  }, [message.messageId]);
  const resetClassificationFields = (targetMessage = message) => {
    setMessageClassification(getInitialClassificationState(targetMessage));
  };
  const liveUpdateMsgClassificationFields = (messageClassification, fieldToUpdate) => {
    const all = getMessageClassificationPayload(message, messageClassification);
    const payload = {
      [fieldToUpdate]: all[fieldToUpdate]
    };
    if (fieldToUpdate === "responseCode") {
      payload.responseCodeDesc = all.responseCodeDesc;
    }
    classifyMessage(message, payload, result => {
      if (result.hasError) {
        addFailureNotification("Failed to classify, please try again");
      }
    });
  };
  const setMessageClassificationFieldValue = value => {
    log.debug(value);
    //set pristine to be false
    value[Object.keys(value)[0]].pristine = false;
    const newClassificationData = {
      ...messageClassification,
      hasTouched: true,
      ...value
    };
    setMessageClassification(newClassificationData);
    if (ClientContext._appName !== "crowd_manager") {
      const liveFieldKeyName = liveUpdateFields.find(fieldName => typeof value[fieldName] !== "undefined");
      if (liveFieldKeyName && value[liveFieldKeyName] && value[liveFieldKeyName].doLiveUpdate === true) {
        liveUpdateMsgClassificationFields(newClassificationData, liveFieldKeyName);
      }
    }
  };
  const submitMessageClassification = () => {
    toggleMessageLoading();
    const payload = getMessageClassificationPayload(message, messageClassification);
    log.debug(payload);
    if (Object.keys(payload).length === 0) {
      clearMessageAndUnclaim();
    } else {
      classifyMessage(message, payload, result => {
        if (result.hasError) {
          addFailureNotification("Failed to finish task. Please try again.");
          /** keeps in inbox */
          clearMessage();
        } else {
          /** removed an unclaims */
          clearMessageAndUnclaim();
        }
      });
    }
  };
  return {
    message,
    messageClassification,
    setMessageClassificationFieldValue,
    submitMessageClassification,
    resetClassificationFields
  };
};